import InfiniteScroll from "react-infinite-scroll-component";
import {
  List,
  ListItem,
  ListItemText,
  Typography,
  FormControl,
  Stack,
  InputAdornment,
  OutlinedInput,
  Box,
  Checkbox,
  ListItemIcon,
  ListItemButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useEffect, useState } from "react";
import { Loader } from "./Loader";
import { useStoreSelectDialog } from "../hooks/useStoreSelectDialog";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import { arraySort } from "../../utils";
import CustomDialogFooter from "./CustomDialogFooter";

export const StoreSelectDialog = (props) => {
  const {
    onSelect,
    isSelected,
    disabled,
    onSubmit,
    isCheckIndeterminate,
    selectedStores,
    alreadySelectedStores,
    handleClose,
    setState,
  } = props;
  const {
    listQuery: { data, isLoading },
    state,
    HandleSearchFilter,
  } = useStoreSelectDialog();

  // If you want to calculate the grand total of all stores
  const grandTotalStores = data?.reduce(
    (total, region) => total + region.stores.length,
    0,
  );

  const handleSelectAll = (e) => {
    const checked = e.target.checked;

    if (checked) {
      // Select all stores
      const allStoreIds = data.flatMap((region) =>
        region.stores.map((store) => store.id),
      );
      setState((draft) => {
        draft.selectStoreDialogDetails.selectedStores = allStoreIds;
      });
      onSelect(data, { target: { checked: true } });
    } else {
      // Deselect all stores
      setState((draft) => {
        draft.selectStoreDialogDetails.selectedStores = [];
      });
      onSelect(data, { target: { checked: false } });
    }
  };
  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <Stack
        justifyContent="space-between"
        gap="8px"
        maxHeight="calc(90vh - 70px)"
        minHeight="400px"
      >
        <Stack maxHeight="calc(90vh - 150px)" gap="16px">
          <Stack px="16px" pt="16px">
            <FormControl size="small" fullWidth={true}>
              <OutlinedInput
                placeholder="Search"
                size="small"
                value={state.search}
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon size="large" />
                  </InputAdornment>
                }
                endAdornment={
                  state.search && (
                    <InputAdornment
                      sx={{ cursor: "pointer" }}
                      onClick={(e) => {
                        HandleSearchFilter("initial", "");
                      }}
                      position="end"
                    >
                      <CloseIcon size="large" />
                    </InputAdornment>
                  )
                }
                fullWidth
                onChange={(e) => {
                  HandleSearchFilter("search", e.target.value);
                }}
              />
            </FormControl>
          </Stack>

          {data?.length > 0 ? (
            <Box
              id="scrollableDiv"
              sx={{ height: "100%", overflowY: "auto" }}
              fullWidth
            >
              <ListItem dense>
                <ListItemButton
                  role={undefined}
                  dense
                  sx={{
                    borderRadius: "8px",
                  }}
                  selected={
                    selectedStores?.length > 0 &&
                    selectedStores?.length === grandTotalStores
                  }
                >
                  <ListItemIcon
                    sx={{
                      minWidth: "32px",
                    }}
                  >
                    <Checkbox
                      edge="end"
                      size="small"
                      disableRipple
                      disabled={
                        alreadySelectedStores?.length === grandTotalStores
                      }
                      checked={
                        selectedStores?.length > 0 &&
                        selectedStores?.length === grandTotalStores
                      }
                      indeterminate={
                        selectedStores?.length > 0 &&
                        selectedStores?.length < grandTotalStores
                      }
                      onChange={(e) => handleSelectAll(e)}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" p={1}>
                        All Stores
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>

              <InfiniteScroll
                scrollableTarget="scrollableDiv"
                dataLength={data?.length}
                next={null}
                hasMore={null}
                loader={
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    Loading...
                  </Box>
                }
              >
                <List sx={{ width: "100%", py: "2px" }}>
                  {arraySort(data, "name").map((value) => {
                    // Sort stores alphabetically by name
                    const stores = Array.isArray(value?.stores)
                      ? arraySort(value.stores, "name")
                      : [];

                    // Filter stores to only include those matching the search input
                    const filteredStores = stores.filter((store) =>
                      store.name
                        .toLowerCase()
                        .includes(state.search.toLowerCase()),
                    );

                    // If there's a search term, and no stores match in this region, skip it
                    if (state.search && filteredStores.length === 0)
                      return null;

                    return (
                      <>
                        {stores.length === 0 ? (
                          ""
                        ) : (
                          <ListOfState
                            key={value.id}
                            value={{ ...value, stores: filteredStores }}
                            labelId={`checkbox-list-label-${value.id}`}
                            onSelect={onSelect}
                            isSelected={isSelected}
                            isCheckIndeterminate={isCheckIndeterminate}
                            selectedStores={selectedStores}
                            alreadySelectedStores={alreadySelectedStores}
                            forceOpen={!!state.search}
                          />
                        )}
                      </>
                    );
                  })}
                </List>
              </InfiniteScroll>
            </Box>
          ) : (
            <Stack height="calc(90vh - 400px)">
              <Typography
                sx={{ display: "inline" }}
                component="span"
                variant="caption"
                color="text.secondary"
                fontWeight={400}
                textAlign="center"
                my="auto"
              >
                There is no stores found
              </Typography>
            </Stack>
          )}
        </Stack>
        <CustomDialogFooter
          disabled={disabled}
          handleClose={handleClose}
          onSubmit={onSubmit}
        />
      </Stack>
    </>
  );
};

function ListOfState({
  value,
  labelId,
  onSelect,
  selectedStores,
  alreadySelectedStores,
  forceOpen,
}) {
  const [open, setOpen] = useState(forceOpen);

  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
    }
  }, [forceOpen]);

  const isSelectedCheck = (value) => {
    if (value.stores) {
      const subArray = value?.stores?.map((e, i) => {
        return e.id;
      });
      const commonIDs1 = selectedStores.filter((val) => subArray.includes(val));
      return subArray.every((val) => commonIDs1.includes(val));
    } else {
      return isSelected(value.id);
    }
  };
  const isSelected = (id) => {
    if (selectedStores.includes(id)) {
      return true;
    } else {
      return selectedStores.includes(id);
    }
  };
  const isDisabled = (value) => {
    return alreadySelectedStores.includes(value);
  };
  const isDisable = (value) => {
    if (value.stores) {
      const subArray = value?.stores?.map((e, i) => {
        return e.id;
      });
      const commonIDs1 = selectedStores.map((val) => subArray.includes(val));
      return subArray.every((val) => commonIDs1.includes(val));
    } else {
      return selectedStores.includes(value.id);
    }
  };

  return (
    <>
      <ListItem key={value} dense>
        <ListItemButton
          role={undefined}
          dense
          sx={{
            borderRadius: "8px",
          }}
          selected={isSelectedCheck(value)}
        >
          <ListItemIcon
            sx={{
              minWidth: "32px",
            }}
          >
            <Checkbox
              edge="end"
              disabled={isDisable(value)}
              checked={isSelectedCheck(value)}
              onChange={(e) => {
                onSelect(value, e);
                setOpen(true);
              }}
              disableRipple
              inputProps={{ "aria-labelledby": labelId }}
            />
          </ListItemIcon>
          <ListItemText
            id={labelId}
            primary={value.name}
            onClick={() => setOpen(!open)}
          />
          <ExpandMoreIcon
            sx={{
              color: "#999999",
              transform: open ? "rotate(180deg)" : "",
              transition: "transform 0.3s",
            }}
            onClick={() => setOpen(!open)}
          />
        </ListItemButton>
      </ListItem>
      {open &&
        value.stores.map((v, index) => {
          const selected = isSelected(v.id);
          return (
            <ListItem key={index} dense>
              <ListItemButton
                role={undefined}
                dense
                selected={selected}
                sx={{ borderRadius: "8px" }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: "32px",
                    marginLeft: "12px",
                  }}
                >
                  <Checkbox
                    edge="edge"
                    checked={selected}
                    disabled={isDisabled(v.id)}
                    onChange={() => onSelect(v)}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText primary={v.name} />
              </ListItemButton>
            </ListItem>
          );
        })}
    </>
  );
}
